'use client'

import { useState }   from 'react'
import { signIn }     from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'

export default function LoginPage() {
  const router       = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl  = searchParams.get('callbackUrl') ?? '/'

  // Arriver sur /login sans un mot d'explication, c'est ne pas savoir si on
  // s'est déconnecté, si on a été déconnecté, ou si rien n'a marché.
  const notice =
    searchParams.get('error') === 'SessionExpired' ? 'Votre session a expiré — reconnectez-vous.'
    : searchParams.get('signedout') ? 'Vous êtes déconnecté, ici et chez le fournisseur d\'identité.'
    : null

  const [tab,      setTab]      = useState<'login' | 'register'>('login')
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [name,     setName]     = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)

    if (tab === 'register') {
      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? ''
        const res = await fetch(`${apiUrl}/auth/register`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ email, password, name }),
        })
        const data = await res.json() as { error?: string }
        if (!res.ok) throw new Error(data.error ?? 'Erreur lors de la création')
        setTab('login')
        setError(null)
        setLoading(false)
        return // retour anticipé pour éviter le double signIn
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erreur inconnue')
        setLoading(false)
        return
      }
    }

    const result = await signIn('credentials', {
      email,
      password,
      redirect:    false,
      callbackUrl,
    })

    if (result?.error) {
      setError('Courriel ou mot de passe incorrect')
      setLoading(false)
      return
    }

    router.push(callbackUrl)
    router.refresh()
  }

  function handleAuthentik() {
    signIn('authentik', { callbackUrl })
  }

  function handleGoogle() {
    signIn('google', { callbackUrl })
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}
      // Le suppressHydrationWarning au niveau du conteneur le plus haut
      // permet d'ignorer les attributs supplémentaires injectés par les extensions
      suppressHydrationWarning
    >
      <div style={{ width: '100%', maxWidth: '400px', animation: 'fadeUp 0.3s var(--ease-out)' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div className="font-display" style={{ fontSize: '28px', fontWeight: 800, color: 'var(--white)', letterSpacing: '-0.03em' }}>
            Amiral<span style={{ color: 'var(--accent)' }}>.</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '4px' }}>
            Connexion à votre espace
          </div>
        </div>

        {/* Card */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '28px' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', marginBottom: '24px', gap: '8px' }}>
            {(['login', 'register'] as const).map(t => (
              <button key={t} onClick={() => { setTab(t); setError(null) }} style={{
                flex: 1, padding: '9px',
                borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: tab === t ? 'rgba(79,124,255,0.15)' : 'var(--bg-panel)',
                color:      tab === t ? 'var(--accent)' : 'var(--text-muted)',
                fontSize: '13px', fontFamily: 'var(--font-mono)',
                fontWeight: tab === t ? 600 : 400,
                transition: 'all 0.15s',
                borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
              }}>
                {t === 'login' ? 'Connexion' : 'Créer un compte'}
              </button>
            ))}
          </div>

          {/* Bouton Google */}
          <button
            onClick={handleGoogle}
            style={{
              width: '100%', padding: '11px', marginBottom: '10px',
              background: 'var(--bg-panel)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              color: 'var(--text-secondary)',
              fontSize: '13px', fontFamily: 'var(--font-mono)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#4285f4'; e.currentTarget.style.color = 'var(--white)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
          >
            <GoogleIcon />
            Continuer avec Google
          </button>

          {/* Bouton Authentik SSO */}
          <button
            onClick={handleAuthentik}
            style={{
              width: '100%', padding: '11px', marginBottom: '20px',
              background: 'var(--bg-panel)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              color: 'var(--text-secondary)',
              fontSize: '13px', fontFamily: 'var(--font-mono)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            <span style={{ fontSize: '16px' }}>🔑</span>
            Continuer avec Authentik SSO
          </button>

          {/* Séparateur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border-dim)' }} />
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>ou</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border-dim)' }} />
          </div>

          {/* Formulaire – suppression de l'hydratation strict pour éviter les conflits d'extensions */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} suppressHydrationWarning>
            {tab === 'register' && (
              <InputField label="Nom" type="text" value={name} onChange={setName} placeholder="Jean-Luc Tremblay" required={false} />
            )}
            <InputField label="Courriel" type="email" value={email} onChange={setEmail} placeholder="vous@example.com" />
            <InputField
              label="Mot de passe" type="password" value={password} onChange={setPassword}
              placeholder={tab === 'register' ? 'Minimum 8 caractères' : '••••••••'}
            />

            {notice && !error && (
              <div style={{
                padding: '10px 14px',
                background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)',
                borderRadius: '8px', fontSize: '12px', fontFamily: 'var(--font-mono)', color: '#93c5fd',
              }}>
                {notice}
              </div>
            )}

            {error && (
              <div style={{
                padding: '10px 14px',
                background: 'rgba(255,107,91,0.1)', border: '1px solid rgba(255,107,91,0.3)',
                borderRadius: '8px', fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--coral)',
              }}>
                ⚠️ {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              padding: '12px', marginTop: '4px',
              background: loading ? 'var(--bg-panel)' : 'var(--accent)',
              border: 'none', borderRadius: '10px',
              color: 'white', fontSize: '14px', fontFamily: 'var(--font-mono)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1, fontWeight: 600, transition: 'all 0.15s',
            }}>
              {loading ? '…' : tab === 'login' ? 'Se connecter' : 'Créer mon compte'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          Amiral Blanc — Plateforme intelligente québécoise 🍁
        </div>
      </div>
    </div>
  )
}

// ─── Google icon SVG ──────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
    </svg>
  )
}

// ─── InputField avec suppressHydrationWarning pour ignorer les modifications d'extensions ──────────────
function InputField({ label, type, value, onChange, placeholder, required = true }: {
  label: string; type: string; value: string
  onChange: (v: string) => void; placeholder?: string; required?: boolean
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>
        {label.toUpperCase()}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '10px 12px',
          color: 'var(--white)',
          fontSize: '13px',
          fontFamily: 'var(--font-mono)',
          outline: 'none',
          transition: 'border-color 0.15s',
        }}
        onFocus={e => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
        // 🔥 Ignorer les attributs ajoutés par les gestionnaires de mots de passe
        suppressHydrationWarning
      />
    </div>
  )
}
